import type { LucideIcon } from "lucide-react";
import {
  AppWindow,
  Archive,
  Armchair,
  ArrowUpDown,
  Bath,
  BatteryCharging,
  Bike,
  Box,
  CircleGauge,
  BrickWall,
  Building,
  Building2,
  ChefHat,
  CircleParking,
  Cog,
  Columns3,
  Construction,
  Cpu,
  DoorOpen,
  Droplets,
  Fan,
  Fence,
  FireExtinguisher,
  Footprints,
  Frame,
  Gauge,
  Grid2x2,
  Grid3x3,
  Heater,
  Home,
  Layers,
  Lightbulb,
  Microwave,
  Mountain,
  Package,
  PaintRoller,
  Paintbrush,
  Palette,
  PanelTop,
  Pipette,
  PlugZap,
  Projector,
  Rows3,
  Ruler,
  Shovel,
  Signpost,
  Siren,
  Sofa,
  SprayCan,
  SquareDashed,
  Sun,
  Thermometer,
  Toilet,
  TrafficCone,
  TreePine,
  Umbrella,
  Warehouse,
  Waves,
  Wifi,
  Wrench,
  Zap,
} from "lucide-react";
import { isAssetType, nlsfb } from "@klantpunt/shared";
import { AssetTypeSymbol } from "./symbols";
import { thumbUrl } from "../../lib/photos";

/** Lijn-iconen per NLBE-SfB-element (namen uit `packages/shared/src/nlsfb.ts`). */
const ICONS: Record<string, LucideIcon> = {
  AppWindow,
  Archive,
  Armchair,
  ArrowUpDown,
  Bath,
  BatteryCharging,
  Bike,
  Box,
  BrickWall,
  Building,
  Building2,
  ChefHat,
  CircleParking,
  Cog,
  Columns3,
  Construction,
  Cpu,
  DoorOpen,
  Droplets,
  Fan,
  Fence,
  FireExtinguisher,
  Footprints,
  Frame,
  Gauge,
  Grid2x2,
  Grid3x3,
  Heater,
  Home,
  Layers,
  Lightbulb,
  Microwave,
  Mountain,
  Package,
  PaintRoller,
  Paintbrush,
  Palette,
  PanelTop,
  Pipette,
  PlugZap,
  Projector,
  Rows3,
  Ruler,
  Shovel,
  Signpost,
  Siren,
  Sofa,
  SprayCan,
  SquareDashed,
  Sun,
  Thermometer,
  Toilet,
  TrafficCone,
  TreePine,
  Umbrella,
  Warehouse,
  Waves,
  Wifi,
  Wrench,
  Zap,
};

/** Pictogram van een element (lijn-icoon, kleur via currentColor); een meter heeft altijd de meter. */
export function AssetGlyph({
  code,
  size = 18,
  meter = false,
  type,
}: {
  code: string | null | undefined;
  size?: number;
  meter?: boolean;
  /** Assettype: eigen symbool (docs/SYMBOLEN.md); zonder type het symbool van het element. */
  type?: string | null;
}) {
  if (!meter && isAssetType(type)) return <AssetTypeSymbol type={type} size={size} />;
  const Icon = meter ? CircleGauge : (ICONS[nlsfb(code).group.icon] ?? Box);
  return <Icon size={size} aria-hidden="true" />;
}

/**
 * Pictogram in een gekleurd rondje (kleur van de hoofdgroep): in lijsten, formulieren en als
 * markering op de plattegrond.
 */
export function AssetBadge({
  code,
  size = 28,
  title,
  meter = false,
  type,
}: {
  /** NLBE-SfB-code ("56.24"); pictogram en kleur van het element. */
  code: string | null | undefined;
  size?: number;
  title?: string;
  /** Asset van soort Meter (docs/METERSTANDEN.md): meter-pictogram. */
  meter?: boolean;
  /** Assettype: eigen symbool; de kleur blijft die van de hoofdgroep. */
  type?: string | null;
}) {
  const { color } = nlsfb(code);
  return (
    <span
      className="assetbadge"
      style={{ background: color, width: size, height: size }}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <AssetGlyph code={code} size={Math.round(size * 0.58)} meter={meter} type={type} />
    </span>
  );
}

/**
 * Herkenningsfoto van een asset (de eerste foto) met het pictogram in de hoek;
 * zonder foto alleen het pictogram.
 */
export function AssetThumb({
  asset,
  size = 34,
}: {
  asset: {
    elementCode: string | null;
    photoId: string | null;
    code: string;
    name: string;
    assetKind?: string;
    assetType?: string | null;
  };
  size?: number;
}) {
  const meter = asset.assetKind === "meter";
  if (!asset.photoId) return <AssetBadge code={asset.elementCode} size={size} meter={meter} type={asset.assetType} />;
  return (
    <span className="assetthumb" style={{ width: size, height: size }}>
      <img src={thumbUrl(asset.photoId)} alt="" loading="lazy" />
      <AssetBadge
        code={asset.elementCode}
        size={Math.max(16, Math.round(size * 0.45))}
        meter={meter}
        type={asset.assetType}
      />
    </span>
  );
}
